import { useCallback, useEffect, useState } from 'react';

export type ThemeChoice = 'system' | 'light' | 'dark';

const KEY = 'lp:color-scheme';

/**
 * Theme is the system preference by default — the CSS already resolves that
 * through `color-scheme: light dark`, so if this hook never ran the page would
 * still be correctly themed. A pinned choice is an override written to
 * :root[data-theme] and the color-scheme meta tag (which keeps the browser
 * chrome in step).
 */
function apply(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', choice);
  }
  const meta = document.querySelector('meta[name="color-scheme"]');
  if (meta) {
    meta.setAttribute('content', choice === 'system' ? 'light dark' : choice);
  }
}

function read(): ThemeChoice {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* private mode or blocked storage — system default is the right answer */
  }
  return 'system';
}

export function useTheme(): { choice: ThemeChoice; setChoice: (c: ThemeChoice) => void } {
  const [choice, setChoiceState] = useState<ThemeChoice>(read);

  useEffect(() => {
    apply(choice);
  }, [choice]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* the choice still applies for this page load */
    }
  }, []);

  return { choice, setChoice };
}

/** Run before React mounts to avoid a flash for anyone with a pinned theme. */
export function initTheme(): void {
  apply(read());
}
